import styled, { css } from 'styled-components';
import { pxToRem } from 'modules/spacing';
import { theme } from 'modules/tetrisTheme';
const getVerticalPadding = (size) => {
    switch (size) {
        case 'xs':
            return theme.spacing.xs;
        case 'sm':
            return pxToRem(6);
        default:
            return theme.spacing.md;
    }
};
const outlinePadding = css `
  padding: ${(props) => getVerticalPadding(props.$size)} ${(props) => (props.$size === 'xs' ? pxToRem(8) : pxToRem(12))};
  padding-right: ${(props) => `calc(${pxToRem(props.$iconSize)} + ${theme.spacing['2xl']})`};
`;
const linePadding = css `
  padding: ${(props) => getVerticalPadding(props.$size)} 0;
  padding-right: ${(props) => `calc(${pxToRem(props.$iconSize)} + ${props.$size === 'xs' ? pxToRem(8) : theme.spacing.lg})`};
`;
const placeholderStyles = css `
  font-style: ${theme.fontStyle.italic};
  color: ${theme.swatches.warm[700]};
`;
const getFontSize = (size) => {
    if (size === 'xs' || size === 'sm') {
        return theme.fontSize.small;
    }
    return theme.fontSize.medium;
};
export const StyledSelect = styled.select `
  appearance: none; /* hide the native chevron icon */
  border: none;
  position: relative; /* makes it possible to open the select also when clicking on the Chevron icon */
  background-color: transparent;
  flex: 1 1 100%;
  font-size: ${(props) => getFontSize(props.$size)};
  line-height: ${theme.lineHeight.default};
  color: ${theme.semantic.foreground.primary};
  width: 100%;
  text-overflow: ellipsis;
  ${(props) => (props.$variant === 'outline' ? outlinePadding : linePadding)};
  ${(props) => props.$showPlaceholderStyle && placeholderStyles};

  &:focus-visible {
    outline: none;
  }

  &:disabled {
    color: ${theme.swatches.warm[500]};
  }
`;
export const Container = styled.div `
  position: relative;
  width: ${(props) => props.$stretch && '100%;'};
`;
const descriptionInvalidStyles = css `
  color: ${theme.semantic.text.danger};
`;
export const Description = styled.div `
  font: ${theme.fontRole.caption};
  color: ${theme.semantic.foreground.primary};
  display: inline-block;
  margin-top: ${theme.spacing.sm};

  &:has(+ * select:disabled) {
    color: ${theme.swatches.warm[500]};
  }

  &:has(+ * select:focus-visible) {
    color: ${(props) => !props.$invalid && theme.semantic.foreground.primary};
  }

  ${(props) => props.$invalid && descriptionInvalidStyles};
`;
const outlineInputDisabledStyles = css `
  border: ${pxToRem(1)} dashed ${theme.semantic.border.strong};
  box-shadow: none;
  margin: ${pxToRem(1)}; /* prevents the input border from jumping */

  svg {
    color: ${theme.swatches.warm[500]};
  }
`;
const outlineInputDefaultStyles = css `
  background-color: ${theme.semantic.surface.primary};
  box-shadow: 0 0 0 ${pxToRem(1)} ${theme.semantic.border.default}; /* firefox fallback until :has selector is supported */

  &:has(select:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(1)} ${theme.semantic.border.default};
  }

  &:hover:not(:has(select:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.swatches.warm[500]};
  }

  &:has(select:focus-visible:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.swatches.warm[700]};

    svg {
      color: ${theme.semantic.foreground.primary};
    }
  }

  &:has(select:active:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.swatches.warm[700]};
  }

  &:has(select:disabled) {
    ${outlineInputDisabledStyles};
  }
`;
const outlineInputHighlightStyles = css `
  background-color: ${theme.swatches.blue[50]};
  box-shadow: 0 0 0 ${pxToRem(1)} ${theme.swatches.blue[500]};

  &:hover:not(:has(select:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.swatches.blue[500]};
  }

  &:has(select:focus-visible:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.swatches.blue[500]};

    svg {
      color: ${theme.swatches.blue[500]};
    }
  }

  &:has(select:active:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.swatches.blue[500]};
  }

  &:has(select:disabled) {
    ${outlineInputDisabledStyles};
  }
`;
const outlineInputInvalidStyles = css `
  background-color: ${theme.semantic.surface.primary};
  box-shadow: 0 0 0 ${pxToRem(1)} ${theme.semantic.text.danger};

  &:hover:not(:has(select:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.semantic.text.danger};
  }

  &:has(select:focus-visible:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.semantic.text.danger};

    svg {
      color: ${theme.semantic.text.danger};
    }
  }

  &:has(select:active:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.semantic.text.danger};
  }

  &:has(select:disabled) {
    ${outlineInputDisabledStyles};
  }
`;
const lineInputDisabledStyles = css `
  border-bottom: ${pxToRem(1)} dashed ${theme.semantic.border.strong};
  box-shadow: none;
  margin: ${pxToRem(2)} ${pxToRem(2)} ${pxToRem(1)}; /* /* prevents the input border from jumping */

  svg {
    color: ${theme.swatches.warm[500]};
  }
`;
const lineInputDefaultStyles = css `
  background-color: transparent;
  box-shadow: 0 ${pxToRem(1)} 0 0 ${theme.semantic.border.default}; /* firefox fallback until :has selector is supported */

  &:has(select:not(:disabled)) {
    box-shadow: 0 ${pxToRem(1)} 0 0 ${theme.swatches.warm[500]};
  }

  &:hover:not(:has(select:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${theme.swatches.warm[700]};
  }

  &:has(select:focus-visible:not(:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${theme.swatches.warm[700]};

    svg {
      color: ${theme.semantic.foreground.primary};
    }
  }

  &:has(select:active:not(:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${theme.swatches.warm[700]};
  }

  &:has(select:disabled) {
    ${lineInputDisabledStyles};
  }
`;
const lineInputHighlightStyles = css `
  box-shadow: 0 ${pxToRem(1)} 0 0 ${theme.swatches.blue[500]};

  &:hover:not(:has(select:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${theme.swatches.blue[500]};
  }

  &:has(select:focus-visible:not(:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${theme.swatches.blue[500]};

    svg {
      color: ${theme.swatches.blue[500]};
    }
  }

  &:has(select:active:not(:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${theme.swatches.blue[500]};
  }

  &:has(select:disabled) {
    ${lineInputDisabledStyles};
  }
`;
const lineInputInvalidStyles = css `
  box-shadow: 0 ${pxToRem(1)} 0 0 ${theme.semantic.text.danger};

  &:hover:not(:has(select:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${theme.semantic.text.danger};
  }

  &:has(select:focus-visible:not(:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${theme.semantic.text.danger};

    svg {
      color: ${theme.semantic.text.danger};
    }
  }

  &:has(select:active:not(:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${theme.semantic.text.danger};
  }

  &:has(select:disabled) {
    ${lineInputDisabledStyles};
  }
`;
const outlineInputStyles = css `
  border-radius: ${theme.borderRadius.md};
  ${(props) => props.$appearance === undefined && !props.$invalid && outlineInputDefaultStyles};
  ${(props) => props.$appearance === 'highlight' && outlineInputHighlightStyles};
  ${(props) => props.$invalid && outlineInputInvalidStyles};
`;
const lineInputStyles = css `
  ${(props) => props.$appearance === undefined && !props.$invalid && lineInputDefaultStyles};
  ${(props) => props.$appearance === 'highlight' && lineInputHighlightStyles};
  ${(props) => props.$invalid && lineInputInvalidStyles};
`;
export const SelectContainer = styled.div `
  ${(props) => props.$variant === 'outline' && outlineInputStyles};
  ${(props) => props.$variant === 'line' && lineInputStyles};
  display: flex;
  box-sizing: border-box;
  position: relative;
  margin: ${pxToRem(2)}; /* prevent box-shadow border from being hidden/covered in case of container not having any padding */

  transition: box-shadow 0.15s;
`;
export const IconContainer = styled.div `
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 50%;
  right: ${(props) => (props.$variant === 'outline' ? theme.spacing.lg : 0)};
  transform: translateY(-50%);
`;
